import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { ConditionBuilder } from './ConditionBuilder';
import { ICondition, negateCondition, newCondition } from './editorModel';
import { choiceLabel, IInlineChoice } from './richTextModel';

/**
 * Bedingter Text im Satz anlegen und ändern (#1245, Teil 43).
 *
 * »Die Niederschrift wurde [den | dem] Erschienenen vorgelesen.« Gefragt wird in der Sprache der
 * Urkunde: wenn …, dann steht hier …, sonst steht hier … . Ein »sonst« wird zur Gegenbedingung —
 * das Modell kennt keinen eigenen Sonst-Zweig, und so bleibt es bei einer Darstellung.
 */

interface Props {
  open: boolean;
  value: IInlineChoice | null;
  questions: { questionId: string; prompt: string; answerType: string }[];
  idLegalTransaction?: number | null;
  onSave: (_choice: IInlineChoice) => void;
  onDelete?: () => void;
  onClose: () => void;
}

const sameCondition = (a: ICondition | null, b: ICondition | null): boolean =>
  JSON.stringify(a) === JSON.stringify(b);

/** Wenn/dann/sonst aus gespeicherten Alternativen — nur, wenn die zweite genau das Gegenteil ist. */
const fromChoice = (choice: IInlineChoice | null) => {
  const [first, second, ...rest] = choice?.alternatives ?? [];
  const elseMatches =
    second &&
    sameCondition(second.condition, negateCondition(first?.condition));
  return {
    // Neu: gleich eine leere Bedingung — ohne sie bliebe der Baukasten bei »ohne Bedingung« stehen.
    condition: (first?.condition ?? newCondition()) as ICondition | null,
    thenText: first?.text ?? '',
    elseText: elseMatches ? second.text : '',
    // Was sich nicht als Wenn/Dann/Sonst lesen lässt, bleibt unverändert erhalten.
    extra: elseMatches ? rest : [second, ...rest].filter(Boolean),
  };
};

export const InlineChoiceDialog: React.FC<Props> = ({
  open,
  value,
  questions,
  idLegalTransaction,
  onSave,
  onDelete,
  onClose,
}) => {
  const [state, setState] = useState(() => fromChoice(value));
  useEffect(() => {
    if (open) {
      setState(fromChoice(value));
    }
  }, [open, value]);

  const counter = negateCondition(state.condition);
  const valid =
    Boolean(state.condition) &&
    (state.thenText.trim() !== '' || state.elseText.trim() !== '');
  const result: IInlineChoice = {
    alternatives: [
      { condition: state.condition, text: state.thenText },
      ...(counter && state.elseText.trim() !== ''
        ? [{ condition: counter, text: state.elseText }]
        : []),
      ...state.extra,
    ],
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth='md' fullWidth>
      <DialogTitle>Bedingter Text im Satz</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography variant='body2' color='text.secondary'>
            Ein Wort oder eine Wendung, die je nach Vorgang oder Antwort anders
            lautet — zum Beispiel »den« oder »dem« Erschienenen.
          </Typography>
          <Box>
            <Typography variant='subtitle2' sx={{ mb: 1 }}>
              Wenn
            </Typography>
            <ConditionBuilder
              condition={state.condition}
              onChange={condition =>
                setState(current => ({ ...current, condition }))
              }
              questions={questions}
              allowNone={false}
              idLegalTransaction={idLegalTransaction}
            />
          </Box>
          <TextField
            size='small'
            label='dann steht im Satz'
            value={state.thenText}
            onChange={event =>
              setState(current => ({
                ...current,
                thenText: event.target.value,
              }))
            }
            autoFocus
          />
          <TextField
            size='small'
            label='sonst steht im Satz'
            value={state.elseText}
            onChange={event =>
              setState(current => ({
                ...current,
                elseText: event.target.value,
              }))
            }
            disabled={!counter}
            helperText={
              counter
                ? 'Leer lassen, wenn sonst nichts stehen soll.'
                : 'Für diesen Vergleich gibt es kein sicheres Gegenteil — bitte eine zweite Stelle mit eigener Bedingung anlegen.'
            }
          />
          {state.extra.length > 0 && (
            <Typography variant='body2' color='text.secondary'>
              Dazu {state.extra.length} weitere Alternative(n) mit eigener
              Bedingung — sie bleiben unverändert.
            </Typography>
          )}
          <Typography variant='body2'>
            Im Text: <strong>{choiceLabel(result)}</strong>
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        {onDelete && (
          <Button color='error' onClick={onDelete} sx={{ mr: 'auto' }}>
            Entfernen
          </Button>
        )}
        <Button onClick={onClose}>Abbrechen</Button>
        <Button
          variant='contained'
          disabled={!valid}
          onClick={() => onSave(result)}
        >
          Übernehmen
        </Button>
      </DialogActions>
    </Dialog>
  );
};
